/** @jsx jsx */
import * as React from 'react';

import { css, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { USER_TYPING_DEBOUNCE_TIME } from '@helpcenter/constants';
import { FieldLoadingSkeleton } from '@helpcenter/loading';
import { isDarkerBorderEnabledForRichTextArea } from 'feature-flags';
import { FieldBase } from 'view/common/fields/field-base';
import AsyncRichTextEditorV2 from 'view/common/fields/rich-text-editor-v2';
import type { FieldConfigs } from 'view/common/mention-provider';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { token } from '@atlaskit/tokens';
import { isOnlyWhitespaceAdf } from '@atlassian/help-center-common-util/adf';
import type { FieldFormProps } from 'view/common/fields/types';

interface Props extends FieldFormProps<string> {
    defaultValue?: string;
    fieldConfigs?: FieldConfigs;
}

class RichTextArea extends React.Component<Props> {
    onFieldChange = (transformedDoc: JSONDocNode): void => {
        const { id, onChange } = this.props;
        if (onChange) {
            // When the fields content is cleared we reset it's value to an empty string in order to correctly display the dirty form warning
            onChange(id, isOnlyWhitespaceAdf(transformedDoc) ? '' : JSON.stringify(transformedDoc));
        }
    };

    onFieldChangeDebounced = debounce(this.onFieldChange, USER_TYPING_DEBOUNCE_TIME);

    render() {
        const { id, label, required, errorMessage, description, disabled, defaultValue, fieldConfigs } = this.props;
        return (
            <FieldBase
                label={label}
                description={description}
                htmlFor={id}
                required={required}
                errorMessage={errorMessage}
            >
                <div
                    id={id}
                    css={[
                        isDarkerBorderEnabledForRichTextArea() ? wrapperStylesWithDarkerBorder : wrapperStyles,
                        // the nested ternary will be cleaned up with FF cleanup of editor-darker-border
                        // eslint-disable-next-line no-nested-ternary
                        errorMessage
                            ? isDarkerBorderEnabledForRichTextArea()
                                ? errorStylesDarkerBorder
                                : errorStyles
                            : isDarkerBorderEnabledForRichTextArea()
                              ? noErrorStylesDarkerBorder
                              : noErrorStyles,
                    ]}
                    data-test-id="rich-text-area-field"
                    aria-label={label}
                >
                    <AsyncRichTextEditorV2
                        isExpanded
                        editorType="field"
                        isEmojiEnabled
                        disabled={!!disabled}
                        onChange={this.onFieldChangeDebounced}
                        defaultValue={defaultValue}
                        fieldConfigs={fieldConfigs}
                        // TODO: editor currently does not support aria-labelledby, so we are using ariaLabel for now
                        ariaLabel={label}
                        ariaDescribedby={`${id}-field-description`}
                        LoadingComponent={FieldLoadingSkeleton}
                    />
                </div>
            </FieldBase>
        );
    }
}

export default RichTextArea;

// Manual overrides required to theme the editor to look like a request create form field
const wrapperStyles = css({
    flex: '1 1 100%',
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor': {
        borderRadius: token('border.radius.100', '3px'),
        border: '2px solid',
        backgroundColor: token('color.background.input', '#FAFBFC'),
        transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor + div': {
        padding: 0,
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor > div': {
        backgroundColor: token('color.background.input', '#FAFBFC'),
        transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:hover .akEditor': {
        backgroundColor: token('color.background.input.hovered', '#EBECF0'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:hover .akEditor > div': {
        backgroundColor: token('color.background.input.hovered', '#EBECF0'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within .akEditor': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
        borderColor: token('color.border.focused', '#4C9AFF'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within .akEditor > div': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within:hover .akEditor': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
        borderColor: token('color.border.focused', '#4C9AFF'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within:hover .akEditor > div': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
    },
});

const errorStyles = css({
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor': {
        borderColor: token('color.border.danger', '#BF2600'),
    },
});

const noErrorStyles = css({
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor': {
        borderColor: token('color.border.input', '#DFE1E6'),
    },
});

const wrapperStylesWithDarkerBorder = css({
    flex: '1 1 100%',
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor': {
        borderRadius: token('border.radius.100', '3px'),
        border: '1px solid',
        backgroundColor: token('color.background.input', '#FAFBFC'),
        transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor + div': {
        padding: 0,
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor > div': {
        backgroundColor: token('color.background.input', '#FAFBFC'),
        transition: 'background-color 200ms ease-in-out, border-color 200ms ease-in-out',
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:hover .akEditor': {
        backgroundColor: token('color.background.input.hovered', '#EBECF0'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:hover .akEditor > div': {
        backgroundColor: token('color.background.input.hovered', '#EBECF0'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within .akEditor': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
        borderColor: token('color.border.focused', '#4C9AFF'),
        outline: `1px solid ${token('color.border.focused', '#4C9AFF')}`,
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within .akEditor > div': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within:hover .akEditor': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
        borderColor: token('color.border.focused', '#4C9AFF'),
        outline: `1px solid ${token('color.border.focused', '#4C9AFF')}`,
    },
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '&:focus-within:hover .akEditor > div': {
        backgroundColor: token('color.background.input', '#FFFFFF'),
    },
});

const errorStylesDarkerBorder = css({
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor': {
        borderColor: token('color.border.danger', '#BF2600'),
        outline: `1px solid ${token('color.border.danger', '#BF2600')}`,
    },
});

const noErrorStylesDarkerBorder = css({
    // Nested selectors will be addressed in REQIN-2475
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.akEditor': {
        borderColor: token('color.border.input', '#42526E'),
        outline: 'none',
    },
});
